'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import type { ProductDisplayData as Product } from '@eliadmin/core/src/api/products/types/product-types';

import { DeferredCardCartButton } from './deferred-cart-button';
import { AddToFavoritesButton } from '@eliadmin/core/src/components/product/add-to-favorites-button';
import { ProductPrice } from '@eliadmin/core/src/components/product/product-price';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import {
  getProductFilterVariants,
  getProductImage,
  getProductUrl,
  isSellable,
} from '@eliadmin/core/src/utils/product-utils';
import { cn, getContrastYIQ } from '@eliadmin/core/src/utils/style-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import React from 'react';

import { ProductBadge } from '../detail/product-badge';

export type ProductCardProps = {
  product: Product;
  priority?: boolean;
  sizes?: string;
  className?: {
    main?: string;
  };
};

function ProductCardInternal({ product, priority = false, sizes = '282px' }: ProductCardProps) {
  //   const isAuth = useGlobalStore(state => state.config.isAuth);
  const { t } = useTranslations();
  const templateSettings = useGlobalStore(s => s.templateSettings);
  // const api = useGlobalStore(state => state.config.api);
  const filter = useCategoryStore(state => state.filter);
  const isWholesaler = useCustomerStore(state => state.me?.customer.is_wholesaler);

  const filterVariants = getProductFilterVariants(product, filter);
  const image = getProductImage(templateSettings, product, filterVariants);
  const productUrl = getProductUrl(product, []);

  const isDiscounted = getProductPriceDisplay(product, {
    isWholesaler,
    priceWithoutVatFirst: templateSettings?.product_detail?.price_without_vat_first?.enabled,
  }).showPreviousPrice;

  return (
    <div
      className={cn(
        'group transition-all',
        'flex w-full flex-col items-start justify-start gap-3 rounded-[4px] border border-border bg-white p-3',
        'hover:border-transparent',
        'hover:ea-product-card',
      )}
    >
      <Link prefetchOnHover prefetch={false} href={productUrl} className="relative w-full">
        <div className="absolute left-0 top-0 z-10 flex w-full flex-wrap items-start justify-start gap-1">
          {isDiscounted && (
            <ProductBadge text={t('text3265')} background="#FFD3D3" color="#BE0000" />
          )}
          {product.tags?.map(tag => (
            <ProductBadge
              key={tag.name}
              text={tag.name}
              background={tag.color ?? '#eeeeee'}
              color={getContrastYIQ(tag.color ?? '#eeeeee')}
            />
          ))}
        </div>
        <Image
          src={image.url ?? ''}
          alt={resolveImageAlt(image, product?.name)}
          width={0}
          height={0}
          className="aspect-square h-auto w-full object-contain object-center"
          sizes={sizes}
          priority={priority}
          loading={priority ? 'eager' : 'lazy'}
          fetchPriority={priority ? 'high' : undefined}
        />
      </Link>
      <div className="flex w-full flex-col items-center justify-center gap-3">
        <Link prefetchOnHover prefetch={false} href={productUrl} className="w-full">
          <h3 className="line-clamp-2 h-11 text-center text-base font-semibold leading-[1.4] text-black">
            {product.name}
          </h3>
        </Link>
        <Link
          prefetchOnHover
          prefetch={false}
          href={productUrl}
          aria-label={product.name}
          className="flex w-full items-center justify-center"
        >
          <ProductPrice
            product={product}
            className={{ price: 'text-lg font-bold', main: 'w-full items-center justify-center' }}
          />
        </Link>
        <div className="flex w-full items-center justify-between gap-2">
          <DeferredCardCartButton
            product={product}
            showIcon={!!templateSettings.product_teaser?.cart_icon}
            sellable={isSellable(product, 1)}
            className={cn('flex-grow', 'ea-product-card__add-to-cart-button')}
          />
          <AddToFavoritesButton
            slug={product.slug}
            isFavorite={product.is_favorite ?? false}
            productId={product.id}
            buttonSize={'icon'}
            buttonClassName="!rounded-[5px]"
          />
        </div>
      </div>
    </div>
  );
}

export function ProductCard(props: ProductCardProps) {
  return <ProductCardInternal {...props} />;
}
